import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import ProcurementChart from '../components/ProcurementChart';
import AnimatedShaderHero from '../components/AnimatedShaderHero';
import api from '../utils/api';


/* ───────── Animated counter hook ───────── */
const useCounter = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(!startOnView);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [startOnView]);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [started, end, duration]);

  return [count, ref];
};

/* ───────── Fade-in-on-scroll wrapper ───────── */
const FadeSection = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.7s cubic-bezier(.22,1,.36,1) ${delay}s, transform 0.7s cubic-bezier(.22,1,.36,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

/* ═══════════════════════════════════════════
   LANDING PAGE
   ═══════════════════════════════════════════ */
const LandingPage = () => {
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const [statsData, setStatsData] = useState({
    users: 0,
    regulations: 0,
    courses: 0,
    consultations: 0,
    surveys: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await api.get('/public-stats');
        setStatsData(data);
      } catch (err) {
        console.error('Failed to load stats', err);
      }
    };
    fetchStats();
  }, []);

  const [statUsers, refUsers] = useCounter(statsData.users);
  const [statRegs, refRegs] = useCounter(statsData.regulations);
  const [statCourses, refCourses] = useCounter(statsData.courses);
  const [statConsult, refConsult] = useCounter(statsData.consultations);

  /* ── kategori data ── */
  const categories = [
    { icon: 'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z', title: 'Perencanaan Pengadaan', desc: 'Identifikasi kebutuhan, penyusunan spesifikasi, dan HPS', color: '#f97316' },
    { icon: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z', title: 'Pemilihan Penyedia', desc: 'Tender, seleksi, pengadaan langsung, e-purchasing', color: '#3b82f6' },
    { icon: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z', title: 'Manajemen Kontrak', desc: 'Penyusunan, pelaksanaan, perubahan & penyelesaian kontrak', color: '#22c55e' },
    { icon: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z', title: 'E-Procurement', desc: 'SPSE, LPSE, e-tendering, e-katalog, SIKaP', color: '#a855f7' },
    { icon: 'M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z', title: 'Jasa Konstruksi', desc: 'Pengadaan khusus pekerjaan konstruksi pemerintah', color: '#ec4899' },
    { icon: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z', title: 'Pengaduan & Sanksi', desc: 'Mekanisme pengaduan, blacklist, dan sanksi penyedia', color: '#ef4444' },
  ];

  /* ── bank pengetahuan data ── */
  const bankItems = [
    { type: 'Undang-Undang', count: 5, icon: '📜', desc: 'UU Cipta Kerja, UU Jasa Konstruksi, dan regulasi dasar' },
    { type: 'Peraturan Presiden', count: 12, icon: '📋', desc: 'Perpres 16/2018, Perpres 12/2021 dan perubahannya' },
    { type: 'Peraturan LKPP', count: 28, icon: '📑', desc: 'Pedoman pelaksanaan teknis dari LKPP' },
    { type: 'Surat Edaran', count: 15, icon: '📨', desc: 'SE dan panduan implementasi terkini' },
    { type: 'Peraturan Pemerintah', count: 8, icon: '⚖️', desc: 'PP terkait pengadaan dan jasa konstruksi' },
    { type: 'Pedoman & SOP', count: 18, icon: '📘', desc: 'Standar operasional dan pedoman teknis' },
  ];

  /* ── fitur utama ── */
  const features = [
    { icon: 'M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155', title: 'Konsultasi Ahli', desc: 'Tanya langsung ke ahli pengadaan bersertifikat. Dapatkan jawaban resmi dan akurat untuk setiap pertanyaan PBJ Anda.' },
    { icon: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25', title: 'Bank Peraturan', desc: 'Akses lengkap seluruh peraturan pengadaan. Baca langsung di browser atau download PDF untuk referensi offline.' },
    { icon: 'M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342', title: 'Pembelajaran Lengkap', desc: 'Kurikulum terstruktur dari dasar hingga mahir. Lengkap dengan materi pembelajaran yang menarik dan mudah dipahami.' },
  ];

  return (
    <div style={{ background: 'transparent', color: 'var(--text-primary)', minHeight: '100vh', overflow: 'hidden' }}>

      {/* ═══════════ NAVBAR ═══════════ */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
        background: scrolled ? 'var(--bg-header)' : 'rgba(0,0,0,0.1)',
        backdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'blur(4px)',
        borderBottom: scrolled ? '1px solid var(--border-secondary)' : '1px solid rgba(255,255,255,0.1)',
        transition: 'all 0.4s ease',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', height: 72, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src="/logo_ngada.png" alt="Logo Kab Ngada" style={{ width: 42, height: 'auto', objectFit: 'contain' }} />
            </div>
            <div>
              <span style={{ fontWeight: 800, fontSize: '1.125rem', color: 'var(--accent-primary)', letterSpacing: '-0.02em' }}>SI-KOPENG</span>
              <span style={{ display: 'block', fontSize: '0.6875rem', color: 'var(--accent-primary)', marginTop: -2 }}>Kab. Ngada</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="landing-nav-links" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {['Beranda', 'Kategori', 'Bank Peraturan', 'Fitur'].map((item) => (
              <a key={item} href={`#${item.toLowerCase().replace(/\s/g, '-')}`}
                style={{ padding: '8px 16px', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', textDecoration: 'none', borderRadius: 8, transition: 'all 0.2s' }}
                onMouseEnter={e => { e.target.style.color = 'var(--accent-primary)'; e.target.style.background = 'var(--accent-soft)'; }}
                onMouseLeave={e => { e.target.style.color = 'var(--text-secondary)'; e.target.style.background = 'transparent'; }}
              >{item}</a>
            ))}
            <Link to="/survey"
              style={{ padding: '8px 16px', fontSize: '0.875rem', fontWeight: 600, color: 'var(--info)', textDecoration: 'none', borderRadius: 8, transition: 'all 0.2s', background: 'rgba(59, 130, 246, 0.1)' }}
              onMouseEnter={e => { e.target.style.background = 'rgba(59, 130, 246, 0.2)'; }}
              onMouseLeave={e => { e.target.style.background = 'rgba(59, 130, 246, 0.1)'; }}
            >Survey SKM</Link>
            <button onClick={toggleTheme} style={{
              width: 38, height: 38, borderRadius: 10, border: '1px solid var(--border-primary)',
              background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: 'var(--text-secondary)', marginLeft: 8, transition: 'all 0.2s'
            }}>
              {theme === 'dark' ? (
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" /></svg>
              ) : (
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" /></svg>
              )}
            </button>
            <Link to="/login" className="btn btn-ghost" style={{ marginLeft: 4, fontSize: '0.875rem', fontWeight: 600, color: 'var(--accent-primary)' }}>Masuk</Link>
            <Link to="/register" className="btn btn-primary" style={{ fontSize: '0.875rem' }}>Daftar Gratis</Link>
          </div>

          {/* Mobile hamburger */}
          <button className="landing-hamburger" onClick={() => setMenuOpen(!menuOpen)} style={{
            display: 'none', width: 40, height: 40, borderRadius: 10, border: '1px solid var(--border-primary)',
            background: 'var(--bg-secondary)', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-primary)'
          }}>
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              {menuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              }
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="landing-mobile-menu" style={{
            background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-secondary)',
            padding: 20, display: 'flex', flexDirection: 'column', gap: 8
          }}>
            {['Beranda', 'Kategori', 'Bank Peraturan', 'Fitur'].map(item => (
              <a key={item} href={`#${item.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setMenuOpen(false)}
                style={{ padding: '12px 16px', fontSize: '0.9375rem', fontWeight: 500, color: 'var(--text-primary)', textDecoration: 'none', borderRadius: 10, display: 'block' }}
              >{item}</a>
            ))}
            <Link to="/survey" onClick={() => setMenuOpen(false)}
              style={{ padding: '12px 16px', fontSize: '0.9375rem', fontWeight: 600, color: 'var(--info)', textDecoration: 'none', borderRadius: 10, display: 'block', background: 'var(--bg-tertiary)' }}
            >Isi Survey SKM</Link>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button onClick={toggleTheme} className="btn btn-secondary btn-sm" style={{ flex: 0 }}>
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
              <Link to="/login" className="btn btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>Masuk</Link>
              <Link to="/register" className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}>Daftar</Link>
            </div>
          </div>
        )}
      </nav>

      {/* ═══════════ HERO / BERANDA ═══════════ */}
      <section id="beranda" style={{ position: 'relative', overflow: 'hidden', minHeight: '100vh', zIndex: 0 }}>
        {/* WebGL Animated Shader Background */}
        <div style={{ position: 'absolute', inset: 0, zIndex: -20 }}>
          <AnimatedShaderHero />
        </div>

        {/* Darkening overlay for contrast over shader */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.25)', // Reduced opacity for more vibrancy
          pointerEvents: 'none'
        }} />

        {/* Pattern overlay - Removed mask for full coverage */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 2,
          backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
          backgroundSize: '80px 80px', opacity: 1, pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: 'clamp(80px, 12vh, 140px) 24px 100px',
          position: 'relative',
          zIndex: 10,
          minHeight: 'min-content', // Allow content to dictate height
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start'
        }}>
          <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
            {/* Badge */}
            <FadeSection>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px 6px 6px',
                borderRadius: 9999, background: 'var(--accent-soft)', border: '1px solid var(--accent-muted)',
                marginBottom: 28
              }}>
                <span style={{ background: 'var(--accent-primary)', color: 'black', fontSize: '0.6875rem', fontWeight: 700, padding: '3px 10px', borderRadius: 9999, letterSpacing: '0.04em' }}>❤️</span>
                <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--accent-primary)', textShadow: '0 1px 5px rgba(0,0,0,0.5)' }}>Platform Kompetensi Pengadaan Barang & Jasa Secara Elektronik di Kab. Ngada</span>
              </div>
            </FadeSection>

            {/* Headline */}
            <FadeSection delay={0.1}>
              <h1 style={{ fontSize: 'clamp(2.25rem, 5vw, 3.75rem)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.03em', marginBottom: 20, color: '#ffffff', textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
                Pusat Informasi{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #8c0000ff, #8c0000ff, #8c0000ff)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  backgroundSize: '200% 100%', animation: 'gradientShift 4s ease infinite',
                }}>
                  Pengadaan
                </span>
                <br />Barang & Jasa
              </h1>
            </FadeSection>

            <FadeSection delay={0.2}>
              <p style={{ fontSize: 'clamp(1rem, 2vw, 1.1875rem)', lineHeight: 1.65, color: '#e2e8f0', textShadow: '0 2px 4px rgba(0,0,0,0.8)', marginBottom: 36, maxWidth: 560, margin: '0 auto 36px' }}>
                Konsultasi ahli, database peraturan lengkap, dan pembelajaran terstruktur untuk seluruh aspek pengadaan barang & jasa Pemerintah Kabupaten Ngada.
              </p>
            </FadeSection>

            {/* CTA */}
            <FadeSection delay={0.3}>
              <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 48 }}>
                <Link to="/register" className="btn btn-primary btn-lg" style={{
                  padding: '16px 36px', fontSize: '1rem', borderRadius: 14,
                  boxShadow: '0 8px 30px rgba(249,115,22,0.35)', transition: 'all 0.3s ease',
                }}>
                  Mulai Sekarang
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>
                </Link>
                <a href="#fitur" className="btn btn-secondary btn-lg" style={{ padding: '16px 36px', fontSize: '1rem', borderRadius: 14, textDecoration: 'none' }}>
                  Lihat Fitur
                </a>
                <Link to="/survey" className="btn btn-secondary btn-lg" style={{
                  padding: '16px 36px', fontSize: '1rem', borderRadius: 14, textDecoration: 'none',
                  borderColor: 'var(--info)', color: 'white', background: 'rgba(59, 130, 246, 0.2)', backdropFilter: 'blur(8px)'
                }}>
                  Survei SKM
                </Link>
              </div>
            </FadeSection>

            {/* Hero illustration — YouTube Official LKPP Channel */}
            <FadeSection delay={0.45}>
              <div style={{
                background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
                borderRadius: 20, padding: 4, boxShadow: '0 30px 80px rgba(0,0,0,0.2), 0 0 0 1px var(--border-secondary)',
                transform: 'perspective(1200px) rotateX(4deg)', transition: 'transform 0.5s ease',
                maxWidth: 900, margin: '0 auto',
              }}>
                <div style={{
                  borderRadius: 16, overflow: 'hidden', background: '#000',
                  position: 'relative', width: '100%', paddingTop: '56.25%' /* 16:9 Aspect Ratio */
                }}>
                  <iframe
                    src="https://www.youtube.com/embed/videoseries?list=UUhe-LX458R4dQcCEr1Efanw"
                    title="Video Terbaru LKPP RI"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none'
                    }}
                  ></iframe>
                </div>
              </div>
            </FadeSection>
          </div>
        </div>
      </section>

      {/* ═══════════ STATISTIK & GRAFIK ═══════════ */}
      <section style={{ padding: '80px 24px', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-secondary)', borderBottom: '1px solid var(--border-secondary)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 60 }}>
          {/* Angka Statistik */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 32, textAlign: 'center' }}>
            {[
              { ref: refUsers, val: statUsers, suffix: '+', label: 'Pengguna Aktif', icon: '👥' },
              { ref: refRegs, val: statRegs, suffix: '+', label: 'Peraturan', icon: '📜' },
              { ref: refCourses, val: statCourses, suffix: '', label: 'Materi Kursus', icon: '📚' },
              { ref: refConsult, val: statConsult, suffix: '+', label: 'Konsultasi Terlayani', icon: '💬' },
            ].map((s, i) => (
              <FadeSection key={i} delay={i * 0.1}>
                <div ref={s.ref}>
                  <div style={{ fontSize: '1.75rem', marginBottom: 8 }}>{s.icon}</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--accent-primary)', lineHeight: 1 }}>
                    {s.val.toLocaleString()}{s.suffix}
                  </div>
                  <div style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)', marginTop: 6 }}>{s.label}</div>
                </div>
              </FadeSection>
            ))}
          </div>

          {/* Grafik Metrik */}
          <FadeSection delay={0.3}>
            <div style={{ background: 'var(--bg-card)', padding: '40px', borderRadius: '24px', border: '1px solid var(--border-secondary)', boxShadow: '0 20px 40px rgba(0,0,0,0.05)' }}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)' }}>Metrik Data Sistem</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Ikhtisar jumlah data dalam berbagai elemen utama aplikasi.</p>
              </div>
              <ProcurementChart stats={statsData} />
            </div>
          </FadeSection>
        </div>
      </section>

      {/* ═══════════ FITUR UTAMA ═══════════ */}
      <section id="fitur" style={{ padding: '60px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeSection>
            <div style={{ textAlign: 'center', marginBottom: 60 }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--accent-primary)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Fitur Utama</span>
              <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: 8, letterSpacing: '-0.02em' }}>Semua yang Anda Butuhkan</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '1.0625rem', marginTop: 12, maxWidth: 520, margin: '12px auto 0' }}>
                Platform lengkap untuk mendukung seluruh proses pengadaan barang dan jasa pemerintah.
              </p>
            </div>
          </FadeSection>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
            {features.map((f, i) => (
              <FadeSection key={i} delay={i * 0.12}>
                <div style={{
                  background: 'var(--bg-secondary)', border: '1px solid var(--border-secondary)',
                  borderRadius: 20, padding: 36, transition: 'all 0.35s ease', cursor: 'default',
                  position: 'relative', overflow: 'hidden',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 20px 60px rgba(249,115,22,0.1)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-secondary)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'var(--accent-soft)', opacity: 0.5 }} />
                  <div style={{
                    width: 56, height: 56, borderRadius: 16, marginBottom: 20,
                    background: 'linear-gradient(135deg, #f97316, #ea580c)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 6px 20px rgba(249,115,22,0.3)',
                  }}>
                    <svg width="26" height="26" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d={f.icon} />
                    </svg>
                  </div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 10, position: 'relative' }}>{f.title}</h3>
                  <p style={{ fontSize: '0.9375rem', lineHeight: 1.7, color: 'var(--text-secondary)', position: 'relative' }}>{f.desc}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ KATEGORI ═══════════ */}
      <section id="kategori" style={{ padding: '100px 24px', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-secondary)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeSection>
            <div style={{ textAlign: 'center', marginBottom: 60 }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--accent-primary)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Kategori Pengadaan</span>
              <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: 8, letterSpacing: '-0.02em' }}>Seluruh Aspek PBJ</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '1.0625rem', marginTop: 12, maxWidth: 500, margin: '12px auto 0' }}>
                Pelajari dan konsultasikan setiap tahap pengadaan barang/jasa pemerintah.
              </p>
            </div>
          </FadeSection>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {categories.map((cat, i) => (
              <FadeSection key={i} delay={i * 0.08}>
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 16, padding: 24,
                  background: 'var(--bg-card)', border: '1px solid var(--border-secondary)',
                  borderRadius: 16, transition: 'all 0.3s ease', cursor: 'default',
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{
                    width: 48, height: 48, borderRadius: 14, flexShrink: 0,
                    background: `${cat.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke={cat.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d={cat.icon} />
                    </svg>
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 4 }}>{cat.title}</h3>
                    <p style={{ fontSize: '0.8125rem', lineHeight: 1.6, color: 'var(--text-secondary)' }}>{cat.desc}</p>
                  </div>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ BANK PERATURAN ═══════════ */}
      <section id="bank-peraturan" style={{ padding: '100px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeSection>
            <div style={{ textAlign: 'center', marginBottom: 60 }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--accent-primary)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Bank Peraturan</span>
              <h2 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, marginTop: 8, letterSpacing: '-0.02em' }}>Database Regulasi Lengkap</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '1.0625rem', marginTop: 12, maxWidth: 540, margin: '12px auto 0' }}>
                Akses seluruh peraturan pengadaan — dari UU hingga Surat Edaran. Baca online atau download PDF.
              </p>
            </div>
          </FadeSection>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {bankItems.map((item, i) => (
              <FadeSection key={i} delay={i * 0.08}>
                <div style={{
                  padding: 24, background: 'var(--bg-secondary)', border: '1px solid var(--border-secondary)',
                  borderRadius: 16, display: 'flex', alignItems: 'center', gap: 18, transition: 'all 0.3s ease', cursor: 'default',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.transform = 'translateX(4px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-secondary)'; e.currentTarget.style.transform = 'translateX(0)'; }}
                >
                  <div style={{ fontSize: '2rem', width: 52, height: 52, borderRadius: 14, background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {item.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <h3 style={{ fontSize: '0.9375rem', fontWeight: 700 }}>{item.type}</h3>
                      <span style={{
                        background: 'var(--accent-primary)', color: 'white', fontSize: '0.75rem', fontWeight: 700,
                        padding: '2px 10px', borderRadius: 9999, minWidth: 32, textAlign: 'center'
                      }}>{item.count}</span>
                    </div>
                    <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{item.desc}</p>
                  </div>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ CTA SECTION ═══════════ */}
      <section style={{ padding: '100px 24px' }}>
        <FadeSection>
          <div style={{
            maxWidth: 900, margin: '0 auto', textAlign: 'center', padding: '72px 40px',
            borderRadius: 28, position: 'relative', overflow: 'hidden',
            background: 'linear-gradient(135deg, #f59e0b, #d97706, #b45309)',
          }}>
            {/* Decorative circles */}
            <div style={{ position: 'absolute', top: -50, left: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
            <div style={{ position: 'absolute', bottom: -80, right: -40, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 500, height: 500, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />

            <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', fontWeight: 800, color: 'white', marginBottom: 16, position: 'relative', lineHeight: 1.2 }}>
              Siap Meningkatkan Kualitas<br />Pengadaan Barang & Jasa?
            </h2>
            <p style={{ fontSize: '1.0625rem', color: 'rgba(255,255,255,0.8)', marginBottom: 32, maxWidth: 480, margin: '0 auto 32px', position: 'relative', lineHeight: 1.65 }}>
              Bergabung sekarang dan akses seluruh fitur SI-KOPENG secara gratis untuk ASN Kabupaten Ngada.
            </p>
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', position: 'relative' }}>
              <Link to="/register" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '16px 36px',
                background: 'white', color: '#d97706', borderRadius: 14, fontWeight: 700, fontSize: '1rem',
                textDecoration: 'none', boxShadow: '0 8px 25px rgba(0,0,0,0.15)', transition: 'all 0.3s ease',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Daftar Gratis Sekarang
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>
              </Link>
              <Link to="/login" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '16px 32px',
                background: 'rgba(255,255,255,0.15)', color: 'white', borderRadius: 14, fontWeight: 600, fontSize: '1rem',
                textDecoration: 'none', border: '1px solid rgba(255,255,255,0.25)', transition: 'all 0.3s ease',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              >
                Masuk
              </Link>
            </div>
          </div>
        </FadeSection>
      </section>

      {/* ═══════════ FOOTER ═══════════ */}
      <footer style={{ padding: '60px 24px 32px', borderTop: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 40, marginBottom: 48 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src="/logo_ngada.png" alt="Logo" style={{ width: 36, height: 'auto' }} />
                </div>
                <span style={{ fontWeight: 800, fontSize: '1rem' }}>SI-KOPENG</span>
              </div>
              <p style={{ fontSize: '0.8125rem', lineHeight: 1.7, color: 'var(--text-secondary)', maxWidth: 260 }}>
                Sistem Informasi Pengadaan Barang dan Jasa Pemerintah Kabupaten Ngada, Provinsi Nusa Tenggara Timur.
              </p>
            </div>
            <div>
              <h4 style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: 14 }}>Layanan</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['Konsultasi Ahli', 'Bank Peraturan', 'Pembelajaran PBJ', 'E-Procurement Info'].map(l => (
                  <span key={l} style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{l}</span>
                ))}
              </div>
            </div>
            <div>
              <h4 style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: 14 }}>Tautan</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { label: 'LKPP RI', url: 'https://www.lkpp.go.id/' },
                  { label: 'SPSE Kab. Ngada', url: 'https://spse.inaproc.id/ngadakab' },
                  { label: 'Portal Kab. Ngada', url: 'https://portal.ngadakab.go.id' },
                  { label: 'E-Katalog', url: 'https://katalog.inaproc.id/' },
                ].map(l => (
                  <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.target.style.color = 'var(--accent-primary)'}
                    onMouseLeave={e => e.target.style.color = 'var(--text-secondary)'}
                  >{l.label}</a>
                ))}
              </div>
            </div>
            <div>
              <h4 style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: 14 }}>Kontak</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                <span>Bagian Administrasi Pembangunan Setda Kab. Ngada</span>
                <span>Jl. Soekarno - Hatta No.1, Bajawa, NTT</span>
                <span>sikopeng@ngadakab.go.id</span>
              </div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid var(--border-secondary)', paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
              © {new Date().getFullYear()} SI-KOPENG — Pemerintah Kabupaten Ngada
            </span>
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
              Dibangun dengan ❤️ zntx untuk pelayanan publik
            </span>
          </div>
        </div>
      </footer>

      {/* ═══════════ CSS ═══════════ */}
      <style>{`
        @keyframes heroPulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.08); opacity: 1; }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Smooth scroll */
        html { scroll-behavior: smooth; }

        /* Responsive nav */
        @media (max-width: 768px) {
          .landing-nav-links { display: none !important; }
          .landing-hamburger { display: flex !important; }
        }
        @media (min-width: 769px) {
          .landing-mobile-menu { display: none !important; }
        }

        /* Link hover for nav */
        section a:not(.btn) { transition: color 0.2s ease; }
      `}</style>
    </div>
  );
};

export default LandingPage;
